import { RegularMapType } from "@components/map/regularMap/regularMap"
import { WindyMapType } from "@components/map/windyMap/windyMap"
import { useAppDispatch, useAppSelector } from "@redux/app/hooks"
import {
	getMapFiltersPanelState,
	updateMapFiltersPanelState,
} from "@redux/features/mapFiltersPanelState/mapFiltersPanelStateSlice"
import { Tooltip } from "@ui-lib/index"
import { t } from "i18next"
import type { LatLngBounds } from "leaflet"
import { memo, useCallback, useState } from "react"
import {
	MdAir,
	MdCloudySnowing,
	MdFilterAlt,
	MdFitScreen,
	MdOutlineFilterAlt,
} from "react-icons/md"

type MapButtonsProps = {
	map?: WindyMapType | RegularMapType
	fitBounds?: LatLngBounds
}

export const MapButtons = memo(function MapButtons(props: MapButtonsProps) {
	const [isFullScreen, setIsFullScreen] = useState<boolean>(false)
	const [isParticlesAnimation, setIsParticlesAnimation] = useState<boolean>(
		localStorage.getItem("settings_particlesAnim") !== null
			? JSON.parse(localStorage.getItem("settings_particlesAnim")!) === "off"
				? false
				: true
			: true
	)
	const dispatch = useAppDispatch()
	const mapFiltersPanelState = useAppSelector(getMapFiltersPanelState)

	const [isWeatherLayer, setIsWeatherLayer] = useState<boolean>(
		props.map?.getShowMapBoxTiles() ?? true
	)

	const myDocument = document.documentElement

	const mapMethods: Object | null | undefined =
		props.map === undefined ? undefined : Object.getPrototypeOf(props.map)

	const setFullScreen = () => {
		if (isFullScreen !== true) {
			if (myDocument.requestFullscreen !== undefined) {
				myDocument.requestFullscreen()
			}
			setIsFullScreen(true)
		} else {
			if (document.exitFullscreen !== undefined) {
				document.exitFullscreen()
			}
			setIsFullScreen(false)
		}
	}

	const onIsParticleAnimationChanged = useCallback(() => {
		if (props.map === undefined) {
			return
		}
		if ((mapMethods as Object).hasOwnProperty("setParticlesAnimation") === false) {
			return
		}
		;(props.map as WindyMapType).setParticlesAnimation()
		setIsParticlesAnimation(!isParticlesAnimation)
	}, [isParticlesAnimation, mapMethods, props.map])

	const onIsWeatherLayerChanged = useCallback(() => {
		if (props.map === undefined) {
			return
		}
		if ((mapMethods as Object).hasOwnProperty("setMapTilesVisibility") === false) {
			return
		}
		setIsWeatherLayer(props.map?.getShowMapBoxTiles())
		;(props.map as WindyMapType).setMapTilesVisibility()
	}, [props.map, mapMethods])

	const onIsShowFiltersPanelChanged = useCallback(() => {
		dispatch(
			updateMapFiltersPanelState({
				isOpen: mapFiltersPanelState.isOpen === true ? false : true,
			})
		)
	}, [dispatch, mapFiltersPanelState])

	const onFitBounds = useCallback(() => {
		if (props.map === undefined || props.fitBounds === undefined) {
			return
		}
		props.map.fitBounds(props.fitBounds)
	}, [props.fitBounds, props.map])

	return props.map !== undefined ? (
		<div
			id="map-custom-buttons"
			style={{
				height:
					(mapMethods !== undefined &&
					(mapMethods as Object).hasOwnProperty("setParticlesAnimation") === true
						? 96 + 2 - 0.4 + 65
						: 32 + 1 + 65) + (props.fitBounds === undefined ? 0 : 32 + 1),
			}}
			className="pointer-events-none absolute top-[calc(80px-65px)] z-1000 ml-3.5 h-20 w-[35.4px] rounded-b-sm border-0 border-t-0 border-[rgba(0,0,0,0.2)] bg-red-600/0"
		>
			{/* WEATHER DATA TOGGLE */}
			{props.map !== undefined &&
			mapMethods !== undefined &&
			(mapMethods as Object).hasOwnProperty("setParticlesAnimation") === true ? (
				<WeatherLayerTooltipMemo
					isWeatherLayer={isWeatherLayer}
					onIsWeatherLayerChanged={onIsWeatherLayerChanged}
				/>
			) : null}
			{/* PARTITLES ANIMATION TOGGLE */}
			{props.map !== undefined &&
			mapMethods !== undefined &&
			(mapMethods as Object).hasOwnProperty("setParticlesAnimation") === true ? (
				<ParticleAnimationTooltipMemo
					isParticlesAnimation={isParticlesAnimation}
					onIsParticleAnimationChanged={onIsParticleAnimationChanged}
				/>
			) : null}
			{/* FILTERS TOGGLE */}
			{props.map !== undefined ? (
				<MapFiltersTooltipMemo
					isOpen={mapFiltersPanelState.isOpen}
					mapMethods={mapMethods}
					onIsShowFiltersPanelChanged={onIsShowFiltersPanelChanged}
					isLast={props.fitBounds === undefined}
				/>
			) : null}
			{props.map !== undefined && props.fitBounds !== undefined ? (
				<FitBoundsTooltipMemo
					onFitBounds={onFitBounds}
					mapMethods={mapMethods}
				/>
			) : null}
		</div>
	) : // </div>
	null
})

const WeatherLayerTooltipMemo = memo(function WeatherLayerTooltip({
	isWeatherLayer,
	onIsWeatherLayerChanged,
}: {
	isWeatherLayer: boolean
	onIsWeatherLayerChanged: () => void
}) {
	return (
		<Tooltip
			side={"right"}
			customContent={t(isWeatherLayer === true ? "HIDE WEATHER LAYER" : "SHOW WEATHER LAYER")}
		>
			<div className="pointer-events-auto absolute top-16.25 left-0 z-401 h-8 w-[35.4px] border-2 border-y-0 border-[rgba(0,0,0,0.2)]">
				<button
					className={`size-8 border-b-1 border-[rgb(201,201,201)] ${
						isWeatherLayer === true ? "bg-white" : "bg-slate-300 text-slate-700"
					} hover:bg-gray-eee px-1`}
					onClick={onIsWeatherLayerChanged}
				>
					<MdCloudySnowing className={`h-6 w-6`} />
				</button>
			</div>
		</Tooltip>
	)
})

const MapFiltersTooltipMemo = memo(function MapFiltersTooltip({
	mapMethods,
	isOpen,
	onIsShowFiltersPanelChanged,
	isLast = true,
}: {
	mapMethods: Object | null | undefined
	isOpen: boolean
	onIsShowFiltersPanelChanged: () => void
	isLast?: boolean
}) {
	return (
		<Tooltip
			side={"right"}
			customContent={t("MAP FILTERS")}
		>
			<div
				id="globalFilter"
				style={{
					top:
						mapMethods !== undefined &&
						(mapMethods as Object).hasOwnProperty("setParticlesAnimation") === true
							? 64 + 65
							: 64.5,
				}}
				className={`${isLast === false ? "h-8 border-b-0" : "h-8.5 rounded-b-md"} pointer-events-auto absolute left-0 z-401 w-[35.4px] border-2 border-t-0 border-[rgba(0,0,0,0.2)]`}
			>
				<button
					className={`size-8 ${isLast === false ? "border-b-1" : "rounded-b-sm"} ${
						isOpen === true ? "bg-white" : "bg-slate-300 text-slate-700"
					} hover:bg-gray-eee px-1`}
					onClick={onIsShowFiltersPanelChanged}
				>
					{isOpen === true ? (
						<MdFilterAlt className={`size-6`} />
					) : (
						<MdOutlineFilterAlt className={`size-6`} />
					)}
				</button>
			</div>
		</Tooltip>
	)
})

const FitBoundsTooltipMemo = memo(function FitBoundsTooltip({
	mapMethods,
	onFitBounds,
}: {
	mapMethods: Object | null | undefined
	onFitBounds: () => void
}) {
	return (
		<Tooltip
			side={"right"}
			customContent={t("FIT DATA TO VIEW")}
		>
			<div
				style={{
					top:
						mapMethods !== undefined &&
						(mapMethods as Object).hasOwnProperty("setParticlesAnimation") === true
							? 96 + 65
							: 32 + 64.5,
				}}
				className="pointer-events-auto absolute left-0 z-401 h-8.5 w-[35.4px] rounded-b-md border-2 border-t-0 border-[rgba(0,0,0,0.2)]"
			>
				<button
					className="flex size-8 items-center justify-center rounded-b-md bg-white hover:bg-[#f4f4f4]"
					onClick={onFitBounds}
				>
					<MdFitScreen className="size-6" />
				</button>
			</div>
		</Tooltip>
	)
})

const ParticleAnimationTooltipMemo = memo(function ParticleAnimationTooltip({
	isParticlesAnimation,
	onIsParticleAnimationChanged,
}: {
	isParticlesAnimation: boolean
	onIsParticleAnimationChanged: () => void
}) {
	return (
		<Tooltip
			side={"right"}
			customContent={t(
				isParticlesAnimation === false
					? "SHOW PARTICLES ANIMATION"
					: "HIDE PARTICLES ANIMATION"
			)}
		>
			<div className="pointer-events-auto absolute top-[calc(32px+65px)] left-0 z-401 h-8 w-[35.4px] border-2 border-y-0 border-[rgba(0,0,0,0.2)]">
				<button
					className={`size-8 border-b-1 border-[rgb(201,201,201)] ${
						isParticlesAnimation === true ? "bg-white" : "bg-slate-300 text-slate-700"
					} hover:bg-gray-eee px-1`}
					onClick={onIsParticleAnimationChanged}
				>
					<MdAir className={`h-6 w-6`} />
				</button>
			</div>
		</Tooltip>
	)
})
